/**
 * IntelligenceTab — promoted brief modification ranking, causal lift,
 * ontology bindings, and linked concepts (grouped by entity_type).
 */

import type { CreativeWorkspaceData, LinkedConcept } from '../types';

export interface BriefModSuggestion {
  rank?: number;
  recommendation?: { title?: string; rationale?: string; change?: string } | null;
  rank_rationale?: string;
  delta?: {
    revenue_impact_delta_usd?: number | null;
    conversion_lift_delta_pct?: number | null;
  } | null;
  added_concept_ids?: string[];
  boosted_concept_ids?: string[];
  suggested_change?: string;
  persona_rationale?: string;
  [key: string]: unknown;
}

export interface BriefModRanking {
  objective?: string;
  suggestions?: BriefModSuggestion[];
  base_revenue_impact_usd?: number | null;
  [key: string]: unknown;
}

interface Props {
  data: CreativeWorkspaceData;
  onApplyToBrief?: (mod: Record<string, unknown>) => void;
  onTestVariant?: (mod: Record<string, unknown>, index: number) => void;
}

export function IntelligenceTab({ data, onApplyToBrief, onTestVariant }: Props) {
  const ranking = (data.ranked_brief_mods ?? null) as BriefModRanking | null;
  const predictiveImpact = data.predictive_impact as Record<string, unknown> | null;
  const ontologyBindings = data.ontology_bindings as
    | { bindings?: Array<Record<string, unknown>> }
    | null;
  const linkedConcepts = data.linked_concepts ?? [];

  return (
    <div className="p-5 space-y-6">
      {ranking ? (
        <RankedBriefModList
          ranking={ranking}
          onApplyToBrief={onApplyToBrief}
          onTestVariant={onTestVariant}
        />
      ) : (
        <RankedBriefModEmptyState />
      )}

      {predictiveImpact && <CausalLiftPanel impact={predictiveImpact} />}

      {ontologyBindings && (ontologyBindings.bindings?.length ?? 0) > 0 && (
        <OntologyBindingTable bindings={ontologyBindings.bindings ?? []} />
      )}

      {linkedConcepts.length > 0 && <LinkedConceptsSection concepts={linkedConcepts} />}
    </div>
  );
}

function RankedBriefModList({
  ranking,
  onApplyToBrief,
  onTestVariant,
}: {
  ranking: BriefModRanking;
  onApplyToBrief?: (mod: Record<string, unknown>) => void;
  onTestVariant?: (mod: Record<string, unknown>, index: number) => void;
}) {
  const suggestions = ranking.suggestions ?? [];
  if (suggestions.length === 0) {
    return <RankedBriefModEmptyState />;
  }
  return (
    <div className="space-y-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Ranked Brief Modifications (by predicted revenue impact)
      </p>
      {suggestions.map((mod, idx) => (
        <BriefModCard
          key={idx}
          rank={idx + 1}
          mod={mod}
          onApplyToBrief={onApplyToBrief}
          onTestVariant={onTestVariant ? () => onTestVariant(mod, idx) : undefined}
        />
      ))}
    </div>
  );
}

function BriefModCard({
  rank,
  mod,
  onApplyToBrief,
  onTestVariant,
}: {
  rank: number;
  mod: BriefModSuggestion;
  onApplyToBrief?: (mod: Record<string, unknown>) => void;
  onTestVariant?: () => void;
}) {
  const delta = mod.delta?.revenue_impact_delta_usd ?? null;
  const lift = mod.delta?.conversion_lift_delta_pct ?? null;
  const title = mod.recommendation?.title ?? mod.suggested_change ?? 'Modification';
  const rationale = mod.recommendation?.rationale ?? mod.persona_rationale ?? '';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2 hover:border-teal-200 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <span className="text-[10px] font-mono text-slate-400 flex-shrink-0 mt-0.5">
            #{rank}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy-900 leading-snug">{title}</p>
            {rationale && (
              <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{rationale}</p>
            )}
          </div>
        </div>
        {delta != null && (
          <div
            className={`flex-shrink-0 text-[11px] font-mono font-semibold px-2 py-1 rounded-lg ${
              delta > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'
            }`}
          >
            {delta > 0 ? '+' : '−'}$
            {Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-[9px] font-normal ml-0.5">est.</span>
          </div>
        )}
      </div>

      {mod.rank_rationale && (
        <p className="text-[11px] text-slate-500 italic leading-relaxed">{mod.rank_rationale}</p>
      )}

      {lift != null && Math.abs(lift) > 1e-6 && (
        <p className="text-[10px] font-mono text-slate-400">
          {(lift * 100).toFixed(1)}% conversion lift Δ
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onApplyToBrief ? () => onApplyToBrief(mod) : undefined}
          disabled={!onApplyToBrief}
          className="text-[11px] text-teal-600 hover:text-teal-700 border border-teal-200 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-md px-2 py-0.5 transition-colors"
        >
          Apply to brief →
        </button>
        <button
          onClick={onTestVariant}
          disabled={!onTestVariant}
          className="text-[11px] text-slate-600 hover:text-teal-600 border border-slate-200 hover:border-teal-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-md px-2 py-0.5 transition-colors"
        >
          Test this variant →
        </button>
      </div>
    </div>
  );
}

function RankedBriefModEmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center space-y-2">
      <p className="text-sm font-medium text-slate-600">
        No brief modification ranking yet
      </p>
      <p className="text-xs text-slate-400">
        Run a creative review to generate PDO-grounded modification suggestions ranked by
        predicted revenue impact.
      </p>
    </div>
  );
}

function CausalLiftPanel({ impact }: { impact: Record<string, unknown> }) {
  const lift = impact.conversion_lift_pct as number | null | undefined;
  const revenue = impact.revenue_impact_usd as number | null | undefined;
  const cac = impact.cac_delta_pct as number | null | undefined;
  const confidence = impact.overall_confidence as number | null | undefined;
  const dominant = impact.dominant_lever as
    | { concept_name?: string; concept_id?: string; weight?: number }
    | null
    | undefined;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Causal Lift
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {lift != null && <Stat label="Lift" value={`${(lift * 100).toFixed(1)}%`} />}
        {revenue != null && (
          <Stat
            label="Revenue impact"
            value={`$${revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          />
        )}
        {cac != null && (
          <Stat label="CAC Δ" value={`${(cac * 100).toFixed(1)}%`} />
        )}
        {confidence != null && (
          <Stat label="Confidence" value={`${(confidence * 100).toFixed(0)}%`} />
        )}
      </div>
      {dominant && dominant.concept_name && (
        <div className="text-xs text-slate-600">
          Dominant lever: <strong>{dominant.concept_name}</strong>
          {dominant.weight != null && (
            <span className="ml-2 font-mono text-[10px] text-slate-400">
              weight {dominant.weight.toFixed(2)}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
      <p className="text-[9px] font-mono uppercase text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-navy-900">{value}</p>
    </div>
  );
}

function OntologyBindingTable({
  bindings,
}: {
  bindings: Array<Record<string, unknown>>;
}) {
  return (
    <section className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Ontology Bindings ({bindings.length})
      </p>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Concept
              </th>
              <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Role
              </th>
              <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Source
              </th>
              <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Confidence
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bindings.slice(0, 50).map((b, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-3 py-1.5 font-medium text-navy-900">
                  {(b.concept_name as string) || (b.concept_id as string) || '—'}
                </td>
                <td className="px-3 py-1.5 text-slate-500 text-[10px] font-mono">
                  {(b.funnel_role as string) || '—'}
                </td>
                <td className="px-3 py-1.5 text-slate-500 text-[10px] font-mono">
                  {(b.binding_source as string) || '—'}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-slate-600">
                  {b.binding_confidence != null
                    ? `${((b.binding_confidence as number) * 100).toFixed(0)}%`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LinkedConceptsSection({ concepts }: { concepts: LinkedConcept[] }) {
  const groups = new Map<string, LinkedConcept[]>();
  for (const c of concepts) {
    const t = (c.entity_type || 'UNKNOWN').toUpperCase();
    const arr = groups.get(t) || [];
    arr.push(c);
    groups.set(t, arr);
  }
  const ordered = Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);

  return (
    <section className="space-y-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Linked Concepts ({concepts.length})
      </p>
      {ordered.map(([type, list]) => (
        <div key={type}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
            {type} <span className="text-slate-300">({list.length})</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {list.map((c) => (
              <span
                key={c.id}
                className="text-[11px] font-mono px-2 py-1 rounded border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-600 transition"
                title={c.review_status ?? ''}
              >
                {c.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
